import React, { useMemo, useState, useEffect } from "react";
import {
  ArrowLeft,
  Search,
  Gavel,
  ShieldAlert,
  ChevronRight,
  ScrollText,
  Plus,
  FileText,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useNav } from "@/context/NavContext";

const PLAYFAIR = "'Playfair Display', Georgia, serif";
const INTER = "'Inter', system-ui, -apple-system, sans-serif";
const GOLD = "#B08A45";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENTS = [
  {
    id: "bail-affidavit",
    title: "Bail Affidavit",
    subtitle: "Affidavit in support of Bail Application",
    Icon: ShieldAlert,
    route: "/criminal/bail-affidavit",
    available: true,
  },
  // Placeholders for future documents (kept greyed out)
  {
    id: "bail-application",
    title: "Bail Application",
    subtitle: "Coming soon",
    Icon: ScrollText,
    available: false,
  },
];

export default function CriminalDocumentsPage() {
  const { navigate } = useNav();
  const [q, setQ] = useState("");
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return DOCUMENTS;
    return DOCUMENTS.filter(
      (d) =>
        d.title.toLowerCase().includes(s) ||
        d.subtitle.toLowerCase().includes(s)
    );
  }, [q]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/bail-cases`, {
          credentials: "include",
        });
        if (res.ok) setCases(await res.json());
      } catch (_) {
        // Non-fatal: user may have no cases yet
      }
      setLoading(false);
    })();
  }, []);

  const openDoc = (doc) => {
    if (!doc.available) {
      toast.info("Coming soon");
      return;
    }
    navigate(doc.route);
  };

  const deleteCase = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this saved case?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/bail-cases/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setCases((prev) => prev.filter((c) => c.id !== id));
        toast.success("Case deleted");
      }
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  return (
    <div
      className="min-h-screen w-full pb-16"
      style={{ background: "#FAF8F4", fontFamily: INTER }}
      data-testid="criminal-documents-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-20 w-full border-b border-stone-200/80 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-4xl items-center gap-2 px-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="btn-back-home"
            className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-stone-100"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 text-stone-700" />
          </button>
          <div className="flex items-center gap-2">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{ background: "linear-gradient(135deg,#C89A4E,#8a6a2f)" }}
            >
              <Gavel className="h-4 w-4 text-white" />
            </div>
            <div
              className="text-[14px] font-semibold text-stone-900"
              style={{ fontFamily: INTER }}
            >
              Criminal Documents
            </div>
          </div>
        </div>
      </header>

      {/* Search */}
      <div className="mx-auto max-w-4xl px-4 pt-6">
        <div
          className="flex items-center gap-2 rounded-2xl bg-white px-4 py-3"
          style={{
            boxShadow:
              "0 10px 30px -20px rgba(15,23,42,0.15), inset 0 0 0 1px rgba(15,23,42,0.06)",
          }}
        >
          <Search className="h-5 w-5 text-stone-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search criminal documents…"
            data-testid="input-criminal-search"
            className="w-full border-0 bg-transparent text-[15px] text-stone-800 outline-none placeholder:text-stone-400"
          />
        </div>
      </div>

      {/* Document list */}
      <div className="mx-auto mt-5 max-w-4xl space-y-3 px-4">
        {filtered.map((doc) => (
          <button
            key={doc.id}
            type="button"
            disabled={!doc.available}
            onClick={() => openDoc(doc)}
            data-testid={`doc-item-${doc.id}`}
            className="group flex w-full items-center gap-4 rounded-2xl bg-white p-4 text-left transition-all duration-300 hover:-translate-y-[1px] disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              boxShadow:
                "0 10px 30px -22px rgba(15,23,42,0.20), inset 0 0 0 1px rgba(15,23,42,0.05)",
            }}
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: "rgba(176,138,69,0.14)",
                boxShadow: "inset 0 0 0 1px rgba(176,138,69,0.32)",
              }}
            >
              <doc.Icon className="h-5 w-5" style={{ color: GOLD }} />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="text-[15.5px] font-semibold text-stone-900"
                style={{ fontFamily: INTER }}
              >
                {doc.title}
              </div>
              <div className="mt-0.5 text-[12.5px] text-stone-500">
                {doc.subtitle}
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-stone-300 transition-transform group-hover:translate-x-1 group-hover:text-stone-500" />
          </button>
        ))}
      </div>

      {/* Saved bail cases */}
      <div className="mx-auto mt-10 max-w-4xl px-4">
        <div className="flex items-center justify-between">
          <h2
            className="text-[15px] font-semibold uppercase tracking-[0.18em] text-stone-500"
            style={{ fontFamily: INTER }}
          >
            Your Saved Bail Cases
          </h2>
          <button
            type="button"
            onClick={() => navigate("/criminal/bail-affidavit")}
            data-testid="btn-new-bail-case"
            className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-medium text-stone-700 hover:bg-stone-100"
          >
            <Plus className="h-4 w-4" /> New
          </button>
        </div>

        <div className="mt-3 space-y-2">
          {loading ? (
            <div className="text-[13px] text-stone-500">Loading…</div>
          ) : cases.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-center text-[13px] text-stone-500"
              style={{ boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.05)" }}
            >
              No saved cases yet. Open Bail Affidavit above to create one.
            </div>
          ) : (
            cases.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => navigate(`/criminal/bail-affidavit/${c.id}`)}
                data-testid={`saved-case-${c.id}`}
                className="flex w-full items-center gap-3 rounded-xl bg-white p-3 text-left transition-all hover:-translate-y-[1px]"
                style={{
                  boxShadow:
                    "0 8px 22px -20px rgba(15,23,42,0.18), inset 0 0 0 1px rgba(15,23,42,0.05)",
                }}
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                  style={{ background: "rgba(15,23,42,0.06)" }}
                >
                  <FileText className="h-4 w-4 text-stone-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-medium text-stone-800">
                    {c.title}
                  </div>
                  <div className="mt-0.5 text-[11px] text-stone-400">
                    Updated {new Date(c.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <span
                  onClick={(e) => deleteCase(c.id, e)}
                  className="rounded-full p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-500"
                  data-testid={`delete-case-${c.id}`}
                  role="button"
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
